import React, { useMemo, useState } from 'react';
import { Box, Button, CircularProgress, Divider, IconButton, Menu, MenuItem, Typography } from '@mui/material';
import PlayArrowOutlinedIcon from '@mui/icons-material/PlayArrowOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import MovieOutlinedIcon from '@mui/icons-material/MovieOutlined';
import { brand, statusTone } from '../theme';
import { formatBytes, formatDate } from '../utils/format';
import StatusChip from './ui/StatusChip';
import EmptyState from './ui/EmptyState';
import { NoteLike, RevisionLike, buildRevisionVersions } from './EditorWorkspace/workflow';
import { DecisionType, isReadyRevision } from './ProjectWorkspace/reviewModel';

interface RevisionsTabProps {
  revisions: RevisionLike[];
  /** Project notes; a reviewer's decision comments are matched to their version. */
  notes?: NoteLike[];
  userRole: 'creator' | 'editor' | 'unauthorized';
  /** False once the project is delivered or cancelled. */
  canDecide?: boolean;
  /** The revision on the player right now. */
  activeId?: string | null;
  /** Versions uploaded after the approved cut that nobody has reviewed. */
  unreviewedAfter?: number[];
  downloadingId?: string | null;
  onPlay?: (revisionId: string) => void;
  onDownload?: (revision: RevisionLike, version: number) => void;
  /** Opens the shared Approve / Request changes confirmation. */
  onDecide?: (type: DecisionType, revisionId: string) => void;
}

/**
 * Every cut the editor uploaded, newest first, as divided rows: version,
 * status, who uploaded it and when, the editor's note and the creator's
 * decision on it. Play puts the version on the page's player; the decision
 * itself lives in one dialog owned by the page.
 */
const RevisionsTab: React.FC<RevisionsTabProps> = ({
  revisions,
  notes = [],
  userRole,
  canDecide = true,
  activeId = null,
  unreviewedAfter = [],
  downloadingId = null,
  onPlay,
  onDownload,
  onDecide,
}) => {
  const versions = useMemo(() => buildRevisionVersions(revisions, notes), [revisions, notes]);
  const [menu, setMenu] = useState<{ id: string; anchor: HTMLElement } | null>(null);

  if (versions.length === 0) {
    return (
      <EmptyState
        compact
        icon={<MovieOutlinedIcon />}
        title="No revisions yet"
        description={
          userRole === 'editor'
            ? 'Upload a cut from the editor workspace and it appears here.'
            : 'When your editor uploads a cut, it appears here and in the player.'
        }
      />
    );
  }

  return (
    <Box component="ol" aria-label="Revisions" sx={{ listStyle: 'none', m: 0, p: 0 }}>
      {versions.map(({ revision, version, decisions }, index) => {
        const decidable = userRole === 'creator' && canDecide && isReadyRevision(revision.status) && Boolean(onDecide);
        const hasMenu = Boolean(revision.previewUrl && onDownload) || decidable;
        const uploader = revision.createdByName || revision.createdBy || 'Your editor';
        const facts = [
          uploader,
          revision.createdAt ? formatDate(revision.createdAt) : null,
          revision.sizeBytes ? formatBytes(revision.sizeBytes) : null,
        ].filter(Boolean);
        return (
          <Box
            component="li"
            key={revision.id}
            data-testid={`revision-v${version}`}
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 2,
              px: 2.5,
              py: 2,
              borderTop: index === 0 ? 'none' : `1px solid ${brand.border}`,
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 1.25, rowGap: 0.5, flexWrap: 'wrap' }}>
                <Typography sx={{ fontWeight: 700 }}>
                  v{version}
                  {index === 0 ? ' · Latest' : ''}
                </Typography>
                <StatusChip kind="revision" status={revision.status} />
                {unreviewedAfter.includes(version) && (
                  <Typography variant="caption" sx={{ fontWeight: 600, color: statusTone.attention.fg }}>
                    Uploaded after the approved cut · not reviewed
                  </Typography>
                )}
                {activeId === revision.id && (
                  <Typography variant="caption" color="text.secondary">
                    In the player
                  </Typography>
                )}
              </Box>
              {revision.filename && (
                <Typography variant="body2" sx={{ mt: 0.5, overflowWrap: 'anywhere' }}>
                  {revision.filename}
                </Typography>
              )}
              <Typography variant="body2" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {facts.join(' · ')}
              </Typography>
              {revision.notes && (
                <Typography variant="body2" sx={{ mt: 1, overflowWrap: 'anywhere' }}>
                  <Box component="span" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                    Editor’s note:{' '}
                  </Box>
                  {revision.notes}
                </Typography>
              )}
              {decisions.map((note) => {
                const changes = String(note.type || '').toUpperCase() === 'REVISION_REQUEST';
                const tone = changes ? statusTone.attention : statusTone.done;
                return (
                  <Box key={note.id} sx={{ mt: 1.25, pl: 1.5, borderLeft: `3px solid ${tone.main}` }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: tone.fg }}>
                      {changes ? 'Changes requested' : 'Approved'}
                      {note.authorName ? ` by ${note.authorName}` : ''}
                      {note.timestamp ? ` · ${formatDate(note.timestamp)}` : ''}
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                      {note.message}
                    </Typography>
                  </Box>
                );
              })}
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
              {revision.previewUrl && onPlay && (
                <Button
                  size="small"
                  startIcon={<PlayArrowOutlinedIcon />}
                  onClick={() => onPlay(revision.id)}
                  aria-label={`Play v${version}`}
                >
                  Play
                </Button>
              )}
              {hasMenu && (
                <>
                  <IconButton
                    aria-label={`More actions for v${version}`}
                    aria-haspopup="menu"
                    onClick={(event) => setMenu({ id: revision.id, anchor: event.currentTarget })}
                  >
                    {downloadingId === revision.id ? <CircularProgress size={18} /> : <MoreVertIcon />}
                  </IconButton>
                  <Menu
                    anchorEl={menu?.id === revision.id ? menu.anchor : null}
                    open={menu?.id === revision.id}
                    onClose={() => setMenu(null)}
                  >
                    {revision.previewUrl && onDownload && (
                      <MenuItem
                        // One download at a time: every Download waits while one runs.
                        disabled={Boolean(downloadingId)}
                        onClick={() => {
                          setMenu(null);
                          onDownload(revision, version);
                        }}
                      >
                        <FileDownloadOutlinedIcon fontSize="small" sx={{ mr: 1.25, color: 'text.secondary' }} />
                        Download
                      </MenuItem>
                    )}
                    {decidable && revision.previewUrl && onDownload && <Divider />}
                    {decidable && (
                      <MenuItem
                        onClick={() => {
                          setMenu(null);
                          onDecide?.('approve', revision.id);
                        }}
                      >
                        Approve
                      </MenuItem>
                    )}
                    {decidable && (
                      <MenuItem
                        onClick={() => {
                          setMenu(null);
                          onDecide?.('changes', revision.id);
                        }}
                      >
                        Request changes
                      </MenuItem>
                    )}
                  </Menu>
                </>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default RevisionsTab;
